import { CheckCircle2, Minus, PackageSearch, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useCart } from "../context/CartContext";
import type { PortalCompany, User } from "../types";

const money = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

interface CheckoutForm {
  companyId: string;
  purchaseOrderNumber: string;
  notes: string;
  postalCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
  recipient: string;
  phone: string;
}

const emptyForm: CheckoutForm = {
  companyId: "", purchaseOrderNumber: "", notes: "", postalCode: "", street: "",
  number: "", complement: "", district: "", city: "", state: "", recipient: "", phone: ""
};

export function CartPage() {
  const navigate = useNavigate();
  const { items, itemCount, updateQuantity, removeItem, clearCart } = useCart();
  const [user, setUser] = useState<User | null>(null);
  const [companies, setCompanies] = useState<PortalCompany[]>([]);
  const [form, setForm] = useState<CheckoutForm>(emptyForm);
  const [loadingAccount, setLoadingAccount] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([
      api<{ user: User | null }>("/api/auth/me"),
      api<{ data: PortalCompany[] }>("/api/companies").catch(() => ({ data: [] }))
    ]).then(([account, companyResult]) => {
      if (!active) return;
      setUser(account.user);
      setCompanies((companyResult.data || []).filter(company => company.company_status === "approved" && company.link_status === "approved"));
      if (account.user) setForm(current => ({ ...current, recipient: current.recipient || account.user!.name, phone: current.phone || account.user!.phone }));
    }).finally(() => active && setLoadingAccount(false));
    return () => { active = false; };
  }, []);

  const visibleTotal = items.reduce((total, item) => total + (item.product.show_price ? Number(item.product.price || 0) * item.quantity : 0), 0);
  const hasHiddenPrice = items.some(item => !item.product.show_price || item.product.price === null);

  function field<K extends keyof CheckoutForm>(key: K, value: CheckoutForm[K]) {
    setForm(current => ({ ...current, [key]: value }));
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSubmitting(true); setError("");
    try {
      await api("/api/orders", {
        method: "POST",
        body: JSON.stringify({
          companyId: form.companyId,
          purchaseOrderNumber: form.purchaseOrderNumber,
          notes: form.notes,
          deliveryAddress: {
            postalCode: form.postalCode, street: form.street, number: form.number,
            complement: form.complement, district: form.district, city: form.city,
            state: form.state.toUpperCase(), recipient: form.recipient, phone: form.phone
          },
          items: items.map(item => ({ productId: item.product.id, quantity: item.quantity }))
        })
      });
      clearCart();
      navigate("/portal?section=orders", { replace: true });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Não foi possível enviar o pedido");
    } finally {
      setSubmitting(false);
    }
  }

  if (!items.length) return <section className="section cart-page"><div className="state-card"><ShoppingCart /><h1>Seu carrinho está vazio</h1><p>Adicione produtos do catálogo para montar o pedido.</p><Link className="button primary" to="/catalogo">Ver produtos</Link></div></section>;

  return <section className="section cart-page">
    <div className="section-heading"><span className="eyebrow">Novo pedido</span><h1>Revisar carrinho</h1><p>{itemCount} item(ns) selecionado(s). Ajuste as quantidades antes de enviar.</p></div>
    <div className="cart-layout">
      <div className="cart-items">
        {items.map(({ product, quantity }) => <article className="cart-item" key={product.id}>
          <div className="cart-thumb">{product.image_url ? <img src={product.image_url} alt={product.name} /> : <PackageSearch />}</div>
          <div className="cart-product"><small>{product.external_code ? `Cód. ${product.external_code}` : `SKU ${product.sku}`}</small><h2>{product.name}</h2>{product.show_price && product.price !== null ? <strong>{money.format(Number(product.price))}</strong> : <span>Preço sob consulta</span>}</div>
          <div className="quantity-control" aria-label={`Quantidade de ${product.name}`}><button type="button" onClick={() => updateQuantity(product.id, quantity - 1)} disabled={quantity <= 1}><Minus size={16} /></button><input aria-label="Quantidade" type="number" min="1" max="100000" value={quantity} onChange={event => updateQuantity(product.id, Number(event.target.value))} /><button type="button" onClick={() => updateQuantity(product.id, quantity + 1)}><Plus size={16} /></button></div>
          <button className="remove-cart-item" type="button" onClick={() => removeItem(product.id)} aria-label={`Remover ${product.name}`}><Trash2 size={18} /></button>
        </article>)}
        <div className="cart-actions"><Link className="button secondary dark" to="/catalogo">Continuar comprando</Link><button className="text-button" type="button" onClick={clearCart}>Esvaziar carrinho</button></div>
      </div>
      <aside className="cart-summary">
        <h2>Resumo</h2><div><span>Itens</span><strong>{itemCount}</strong></div><div><span>Produtos diferentes</span><strong>{items.length}</strong></div>{visibleTotal > 0 && <div className="cart-total"><span>Total conhecido</span><strong>{money.format(visibleTotal)}</strong></div>}{hasHiddenPrice && <small>O valor final será confirmado pela equipe comercial.</small>}
      </aside>
    </div>

    {loadingAccount ? <div className="state-card compact">Verificando seu cadastro...</div> : !user ? <div className="checkout-gate"><ShoppingCart /><div><h2>Entre para enviar o pedido</h2><p>Seu carrinho ficará salvo neste navegador.</p></div><Link className="button primary" to="/entrar">Entrar</Link></div> : !companies.length ? <div className="checkout-gate"><CheckCircle2 /><div><h2>Vincule um CNPJ aprovado</h2><p>O pedido só pode ser enviado para uma empresa já aprovada.</p></div><Link className="button primary" to="/portal?section=companies">Minhas empresas</Link></div> : <form className="checkout-form" onSubmit={submit}>
      <div className="panel-heading"><div><h2>Dados do pedido</h2><p>Selecione a empresa e confirme o endereço de entrega.</p></div></div>
      <div className="form-grid two"><label>Empresa/CNPJ<select required value={form.companyId} onChange={event => field("companyId", event.target.value)}><option value="">Selecione a empresa</option>{companies.map(company => <option key={company.id} value={company.id}>{company.trade_name || company.legal_name} — {company.cnpj}</option>)}</select></label><label>Número da ordem de compra<input value={form.purchaseOrderNumber} onChange={event => field("purchaseOrderNumber", event.target.value)} placeholder="Opcional" /></label></div>
      <h3>Endereço de entrega</h3>
      <div className="form-grid address"><label>CEP<input required value={form.postalCode} onChange={event => field("postalCode", event.target.value)} /></label><label className="wide">Logradouro<input required value={form.street} onChange={event => field("street", event.target.value)} /></label><label>Número<input required value={form.number} onChange={event => field("number", event.target.value)} /></label><label>Complemento<input value={form.complement} onChange={event => field("complement", event.target.value)} /></label><label>Bairro<input required value={form.district} onChange={event => field("district", event.target.value)} /></label><label>Cidade<input required value={form.city} onChange={event => field("city", event.target.value)} /></label><label>UF<input required maxLength={2} value={form.state} onChange={event => field("state", event.target.value.toUpperCase())} /></label><label>Recebedor<input required value={form.recipient} onChange={event => field("recipient", event.target.value)} /></label><label>Telefone<input required value={form.phone} onChange={event => field("phone", event.target.value)} /></label></div>
      <label>Observações<textarea value={form.notes} onChange={event => field("notes", event.target.value)} maxLength={1000} placeholder="Informações adicionais para o pedido ou entrega" /></label>
      {error && <div className="form-error">{error}</div>}
      <button className="button primary checkout-submit" disabled={submitting}>{submitting ? "Enviando pedido..." : "Criar pedido e enviar ao sistema"}</button>
    </form>}
  </section>;
}
